import { maxLength, required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Stack,
  Text,
  TextField,
  TextLink,
} from 'braid-design-system';
import React, {
  type ChangeEvent,
  type FocusEvent,
  type Ref,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { PHONE_NUMBER_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { validateField } from 'src/app/utils/validation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdatePhoneNumber } from 'src/app/v1/state/modules/formData/phoneNumber';
import type { AppState } from 'src/types/appState';
import type { PhoneNumberRef } from 'src/types/formFields';

import translations from './.vocab';
import { cleanPhoneNumber } from './utils';

export const PhoneNumber = forwardRef((_props, ref: Ref<PhoneNumberRef>) => {
  const { t } = useTranslations(translations);

  const { phoneNumber, nation } = useSelector(
    (state: AppState) => ({
      nation: state.session.hirerAccount?.nation,
      phoneNumber: state.formData.phoneNumber,
    }),
    shallowEqual,
  );
  const labelName = t('phone-number-field-label');
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const [inputValue, setInputValue] = useState<string>(phoneNumber || '');
  const dispatch = useDispatch();
  const { getLanguage } = useConfig();

  useImperativeHandle(ref, () => ({
    handleValidation: (skipIfNull = false) =>
      handleInputValidation(inputValue, skipIfNull),
  }));

  const handleInputValidation = (
    value: string,
    skipIfNull = false,
  ): string | null => {
    const validationResult = validateField(
      {
        field: 'phoneNumber',
        validators: [required, maxLength],
        validatorProps: {
          max: 25,
          requiredMessage: t('phone-number-validation-required'),
        },
        value,
      },
      skipIfNull,
      getLanguage(),
    );

    setValidationMessage(validationResult);

    return validationResult;
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const cleanedValue = cleanPhoneNumber(value);
    setInputValue(cleanedValue);
    handleInputValidation(cleanedValue);
  };

  const handleOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    const eventData = {
      eventName: PHONE_NUMBER_PRESSED,
      objectInteraction: 'phone-number',
    };
    getTrackingAdapter.sendLinkEvent(eventData);

    const { value } = event.target;
    const cleanedValue = cleanPhoneNumber(value);
    const errorMessage = handleInputValidation(cleanedValue);

    if (!errorMessage) {
      dispatch(actionUpdatePhoneNumber(cleanedValue));
    }
  };

  return (
    <Stack space="small">
      <Box position="relative">
        <TextField
          id="phoneNumber"
          label={labelName}
          message={validationMessage || t('phone-number-field-default-message')}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          value={inputValue}
          secondaryLabel={t('phone-number-field-secondary-label')}
          tone={validationMessage ? 'critical' : 'neutral'}
          data={{ 'hj-whitelist': 'true' }}
          reserveMessageSpace
        />
        <Box right={0} position="absolute" style={{ top: '-2px' }} />
      </Box>
      <Alert tone="info">
        <Text>
          {t(
            nation === 'HK'
              ? 'phone-number-private-ad-alert-title-hk'
              : 'phone-number-private-ad-alert-title-default',
            {
              Link: (children) => (
                <TextLink
                  href="https://www.pcpd.org.hk/"
                  weight="weak"
                  target="_blank"
                >
                  {children}
                </TextLink>
              ),
            },
          )}
        </Text>
      </Alert>
    </Stack>
  );
});
